import { useCallback, useEffect, useState } from 'react';

// Baked into the bundle at build time (vite.config.ts `define`). The `typeof`
// guard keeps this safe under vitest, where the global isn't defined.
const CURRENT_COMMIT_HASH =
    typeof __APP_COMMIT_HASH__ === 'string' ? __APP_COMMIT_HASH__ : '';

// Safety-net poll for the always-focused trader who never switches tabs or
// navigates. Only fires while the tab is visible — mobile browsers throttle
// background timers anyway. version.json is a tiny same-origin static file.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

interface VersionJson {
    commitHash?: unknown;
    builtAt?: unknown;
}

/**
 * A newer bundle has shipped when we have both hashes and they differ. Bail to
 * `false` on a missing current hash (misconfigured build) or a missing latest
 * (fetch failed / dev) so a flaky read never nags the user with a bogus prompt.
 */
export function computeHasUpdate(
    current: string,
    latest: string | null,
): boolean {
    if (!current || !latest) return false;
    return latest !== current;
}

/**
 * Fetch the deployed bundle's commit hash from /version.json. Cache-busted so
 * no CDN/SW ever hands back a stale file. Returns null on any failure (missing
 * file in dev, transient network error, malformed JSON) — never throws.
 */
export async function fetchLatestCommitHash(): Promise<string | null> {
    try {
        const res = await fetch(`/version.json?t=${Date.now()}`, {
            cache: 'no-store',
        });
        if (!res.ok) return null;
        const data = (await res.json()) as VersionJson;
        return typeof data.commitHash === 'string' && data.commitHash.length > 0
            ? data.commitHash
            : null;
    } catch {
        return null;
    }
}

export function useAppVersion() {
    const [latestCommitHash, setLatestCommitHash] = useState<string | null>(
        null,
    );
    const [isReloading, setIsReloading] = useState(false);

    useEffect(() => {
        // No version.json to diff against under `vite dev` — skip the fetch so
        // we don't spam the console with 404s. Runs in the production bundle.
        if (import.meta.env.DEV) return;

        let cancelled = false;
        const check = async () => {
            if (document.visibilityState !== 'visible') return;
            const hash = await fetchLatestCommitHash();
            if (!cancelled && hash) setLatestCommitHash(hash);
        };

        void check();

        const onVisibilityChange = () => {
            if (!document.hidden) void check();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        const interval = window.setInterval(
            () => void check(),
            POLL_INTERVAL_MS,
        );

        return () => {
            cancelled = true;
            document.removeEventListener(
                'visibilitychange',
                onVisibilityChange,
            );
            window.clearInterval(interval);
        };
    }, []);

    const reload = useCallback(() => {
        if (isReloading) return;
        setIsReloading(true);
        // Defer one tick so the disabled/"Updating…" state paints before the
        // synchronous reload tears the page down — otherwise React never
        // flushes it and the click gives no feedback. InOut's PWA precaches
        // nothing (globPatterns: []), so the reload refetches the full bundle
        // (~1-2s) and that feedback matters. ~60ms is imperceptible. No
        // SKIP_WAITING dance needed since nothing stale can be served.
        window.setTimeout(() => window.location.reload(), 60);
    }, [isReloading]);

    return {
        hasUpdate: computeHasUpdate(CURRENT_COMMIT_HASH, latestCommitHash),
        currentCommitHash: CURRENT_COMMIT_HASH,
        latestCommitHash,
        isReloading,
        reload,
    };
}
